/**
 * VoyagerSearchContext - React Context for Voyager Search functionality
 *
 * This context provides state management and search operations for the
 * VoyagerSearchPageBlock, using the USDA FAS Voyager Search API.
 */

import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import type { ResultListStateQuery } from '../state/state.types';
import type {
  FilterItem,
  LatLonPair,
  Metadata,
  SearchField,
} from '../models/search-result';
import { VoyagerSearchService } from '../Services/VoyagerSearchService';
import type {
  VoyagerFacetFieldConfig,
  VoyagerFacetConfig,
} from '../Services/VoyagerSearchService';
import { logError, logDebug } from '../../../../utils/logger';
import { buildUrlFromQuery, parseQueryFromUrl } from './searchUrlHelpers';
import {
  ensureSearchFields,
  getInitialState,
  initialSearchState,
  normalizeBbox,
  normalizeFilters,
} from './initialState';
import { voyagerSearchReducer } from './searchReducer';
import type {
  BuildSearchUrlOverrides,
  VoyagerSearchContextState,
} from './searchContext.types';

// Context value interface (state + actions)
interface VoyagerSearchContextValue extends VoyagerSearchContextState {
  /**
   * AGOL base URL for this search page. Lives here rather than in a hook that
   * fetched a site-wide settings page: ResultList and ResultListViews are nested
   * inside the block and receive no `data` prop, and each search page now
   * configures its own AGOL instance.
   */
  arcgisUrl: string;
  updateSearchQuery: (query: string) => void;
  updateSearchFields: (fields: SearchField[]) => void;
  updateFilters: (filters: FilterItem[]) => void;
  updateBBox: (bbox: LatLonPair[]) => void;
  updateSpatialFilter: (bbox: LatLonPair[]) => void;
  updatePagination: (pageIndex: number, pageSize: number) => void;
  updateSortBy: (sortBy: string) => void;
  updateSearchScope: (scope: string) => void;
  updateActiveFilter: (filter: string) => void;
  executeSearch: (queryOverride?: ResultListStateQuery) => void;
  applySearchParams: (
    overrides?: BuildSearchUrlOverrides,
    options?: { historyMode?: 'push' | 'replace' },
  ) => void;
  clearSearch: () => void;
  buildSearchUrl: (overrides?: BuildSearchUrlOverrides) => string;
  fetchFacets: () => Promise<void>;
  fetchHeatmap: () => Promise<void>;
  fetchDisplayConfig: () => Promise<void>;
  setHoveredItem: (item: Metadata | null) => void;
  clearHoveredItem: () => void;
}

// Create context
const VoyagerSearchContext = createContext<
  VoyagerSearchContextValue | undefined
>(undefined);

// Provider component props
interface VoyagerSearchProviderProps {
  children: React.ReactNode;
  baseUrl?: string;
  configId?: string;
  sidebarFacetFields?: VoyagerFacetFieldConfig[];
  sidebarFacetParams?: Record<string, string>;
  sidebarFacetLimit?: number;
  sidebarFacetMinCount?: number;
  hierarchyTreeBase?: string;
  arcgisUrl?: string;
}

// Provider component
export const VoyagerSearchProvider: React.FC<VoyagerSearchProviderProps> = ({
  children,
  baseUrl,
  configId,
  sidebarFacetFields,
  sidebarFacetParams,
  sidebarFacetLimit,
  sidebarFacetMinCount,
  hierarchyTreeBase,
  arcgisUrl,
}) => {
  const [state, dispatch] = useReducer(voyagerSearchReducer, getInitialState());
  const stateRef = useRef(state);

  // Configure the VoyagerSearchService with block-specific settings
  useEffect(() => {
    const hasFacetOverrides = Boolean(
      sidebarFacetFields?.length ||
        sidebarFacetParams ||
        sidebarFacetLimit !== undefined ||
        sidebarFacetMinCount !== undefined ||
        hierarchyTreeBase !== undefined,
    );

    if (baseUrl || configId || hasFacetOverrides) {
      const currentFacetConfig = VoyagerSearchService.getConfig().facetConfig;
      const facetConfig: VoyagerFacetConfig = {
        ...currentFacetConfig,
        fields: sidebarFacetFields?.length
          ? sidebarFacetFields
          : currentFacetConfig.fields,
        params: {
          ...(currentFacetConfig.params || {}),
          ...(sidebarFacetParams || {}),
        },
        ...(sidebarFacetLimit !== undefined
          ? { limit: sidebarFacetLimit }
          : {}),
        ...(sidebarFacetMinCount !== undefined
          ? { minCount: sidebarFacetMinCount }
          : {}),
        ...(hierarchyTreeBase !== undefined ? { hierarchyTreeBase } : {}),
      };

      VoyagerSearchService.configure({
        ...(baseUrl && { baseUrl }),
        ...(configId && { configId }),
        ...(hasFacetOverrides ? { facetConfig } : {}),
      });
    }
  }, [
    baseUrl,
    configId,
    sidebarFacetFields,
    sidebarFacetParams,
    sidebarFacetLimit,
    sidebarFacetMinCount,
    hierarchyTreeBase,
  ]);

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  // Build query from overrides
  const buildQuery = useCallback(
    (overrides: BuildSearchUrlOverrides = {}): ResultListStateQuery => {
      const baseQuery =
        stateRef.current.searchState.query || initialSearchState.query!;

      const filters =
        overrides.filters !== undefined
          ? normalizeFilters(overrides.filters)
          : normalizeFilters(baseQuery.filters || []);

      const bbox =
        overrides.bbox !== undefined
          ? normalizeBbox(overrides.bbox)
          : normalizeBbox(baseQuery.spatialFilter?.bbox || []);

      const searchFields =
        overrides.searchFields !== undefined
          ? ensureSearchFields(overrides.searchFields)
          : ensureSearchFields(baseQuery.settings?.searchFields);
      const searchScope =
        overrides.searchScope ?? baseQuery.searchScope ?? 'all';
      const scopeFq =
        'scopeFq' in overrides ? overrides.scopeFq : baseQuery.scopeFq;
      const scopeLabel =
        'scopeLabel' in overrides ? overrides.scopeLabel : baseQuery.scopeLabel;

      // Handle place parameter - use override if provided (null means clear), otherwise use base query
      const place = 'place' in overrides ? overrides.place : baseQuery.place;
      // Convert null to undefined for the final state
      const normalizedPlace = place === null ? undefined : place;
      const spatialOperator =
        overrides.spatialOperator ?? baseQuery.spatialOperator ?? 'within';
      const locationQuery =
        overrides.locationQuery ?? baseQuery.locationQuery ?? '';

      return {
        ...baseQuery,
        filters,
        spatialFilter: {
          ...(baseQuery.spatialFilter || { bbox: [], isChecked: false }),
          bbox,
          isChecked: bbox.length > 0,
        },
        pageIndex: overrides.pageIndex ?? baseQuery.pageIndex ?? 0,
        pageSize: overrides.pageSize ?? baseQuery.pageSize ?? 48,
        sortBy: overrides.sortBy ?? baseQuery.sortBy ?? 'date',
        sortOrder: overrides.sortOrder ?? baseQuery.sortOrder ?? 'desc',
        searchScope,
        scopeFq,
        scopeLabel,
        search: overrides.search ?? baseQuery.search ?? '',
        place: normalizedPlace,
        spatialOperator,
        locationQuery,
        settings: {
          ...(baseQuery.settings || { searchFields: ['any'] }),
          searchFields,
        },
      };
    },
    [],
  );

  // Set query state and update URL
  const setQueryState = useCallback(
    (query: ResultListStateQuery, historyMode: 'push' | 'replace' = 'push') => {
      dispatch({ type: 'SET_QUERY', payload: query });
      stateRef.current = {
        ...stateRef.current,
        searchState: {
          ...stateRef.current.searchState,
          query,
        },
      };

      if (typeof window !== 'undefined') {
        const newUrl = buildUrlFromQuery(query);
        if (historyMode === 'replace') {
          window.history.replaceState(null, '', newUrl);
        } else {
          window.history.pushState(null, '', newUrl);
        }
      }
    },
    [],
  );

  // Execute search using Voyager API
  const executeSearch = useCallback(
    async (queryOverride?: ResultListStateQuery) => {
      dispatch({ type: 'SET_LOADING', payload: true });

      try {
        const query =
          queryOverride ||
          stateRef.current.searchState.query ||
          initialSearchState.query!;
        logDebug('VoyagerSearchContext: Executing search with query', query);

        const result = await VoyagerSearchService.search(query, {
          includeFacets: true,
        });
        result.query = query;

        dispatch({ type: 'SET_SEARCH_RESULTS', payload: result });
      } catch (error) {
        logError('VoyagerSearchContext: Search error', error);
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    },
    [],
  );

  // Fetch facets
  const fetchFacets = useCallback(async () => {
    try {
      const facets = await VoyagerSearchService.fetchFacets();
      dispatch({ type: 'SET_FACETS', payload: facets });
    } catch (error) {
      logError('VoyagerSearchContext: Facet fetch error', error);
    }
  }, []);

  // Fetch heatmap
  const fetchHeatmap = useCallback(async () => {
    try {
      const heatmap = await VoyagerSearchService.fetchHeatmap();
      dispatch({ type: 'SET_HEATMAP', payload: heatmap });
    } catch (error) {
      logError('VoyagerSearchContext: Heatmap fetch error', error);
    }
  }, []);

  const fetchDisplayConfig = useCallback(async () => {
    try {
      const displayConfig = await VoyagerSearchService.fetchDisplayConfig();
      dispatch({ type: 'SET_DISPLAY_CONFIG', payload: displayConfig });
    } catch (error) {
      logError('VoyagerSearchContext: Display config fetch error', error);
    }
  }, []);

  // Action handlers
  const updateSearchQuery = useCallback((query: string) => {
    dispatch({ type: 'UPDATE_SEARCH_QUERY', payload: query });
  }, []);

  const updateSearchFields = useCallback((fields: SearchField[]) => {
    dispatch({ type: 'UPDATE_SEARCH_FIELDS', payload: fields });
  }, []);

  const updateFilters = useCallback((filters: FilterItem[]) => {
    dispatch({ type: 'UPDATE_FILTERS', payload: filters });
  }, []);

  const updateBBox = useCallback((bbox: LatLonPair[]) => {
    dispatch({ type: 'UPDATE_BBOX', payload: bbox });
  }, []);

  const updateSpatialFilter = useCallback((bbox: LatLonPair[]) => {
    dispatch({ type: 'UPDATE_BBOX', payload: bbox });
  }, []);

  const updatePagination = useCallback(
    (pageIndex: number, pageSize: number) => {
      dispatch({ type: 'UPDATE_PAGINATION', payload: { pageIndex, pageSize } });
    },
    [],
  );

  const updateSortBy = useCallback((sortBy: string) => {
    dispatch({ type: 'UPDATE_SORT_BY', payload: sortBy });
  }, []);

  const updateSearchScope = useCallback((scope: string) => {
    dispatch({ type: 'UPDATE_SEARCH_SCOPE', payload: scope });
  }, []);

  const updateActiveFilter = useCallback((filter: string) => {
    dispatch({ type: 'UPDATE_ACTIVE_FILTER', payload: filter });
  }, []);

  const clearSearch = useCallback(() => {
    dispatch({ type: 'CLEAR_SEARCH' });
    setQueryState(initialSearchState.query!, 'replace');
  }, [setQueryState]);

  const setHoveredItem = useCallback((item: Metadata | null) => {
    dispatch({ type: 'SET_HOVERED_ITEM', payload: item });
  }, []);

  const clearHoveredItem = useCallback(() => {
    dispatch({ type: 'SET_HOVERED_ITEM', payload: null });
  }, []);

  const applySearchParams = useCallback(
    (
      overrides: BuildSearchUrlOverrides = {},
      options?: { historyMode?: 'push' | 'replace' },
    ) => {
      const mergedQuery = buildQuery(overrides);
      setQueryState(mergedQuery, options?.historyMode || 'push');
      executeSearch(mergedQuery);
    },
    [buildQuery, executeSearch, setQueryState],
  );

  const buildSearchUrl = useCallback(
    (overrides: BuildSearchUrlOverrides = {}) => {
      const query = buildQuery(overrides);
      return buildUrlFromQuery(query);
    },
    [buildQuery],
  );

  // Initialize from URL and sync with browser history
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const syncFromLocation = (historyMode: 'push' | 'replace' = 'replace') => {
      const urlParams = new URLSearchParams(window.location.search);
      const { query, activeFilter } = parseQueryFromUrl(urlParams);
      setQueryState(query, historyMode);
      dispatch({ type: 'UPDATE_ACTIVE_FILTER', payload: activeFilter });
      executeSearch(query);
    };

    // Initial sync and facet fetch
    syncFromLocation('replace');
    fetchFacets();

    const handlePopState = () => {
      syncFromLocation('replace');
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [executeSearch, setQueryState, fetchFacets]);

  // Context value
  const value: VoyagerSearchContextValue = {
    arcgisUrl: arcgisUrl || '',
    ...state,
    updateSearchQuery,
    updateSearchFields,
    updateFilters,
    updateBBox,
    updateSpatialFilter,
    updatePagination,
    updateSortBy,
    updateSearchScope,
    updateActiveFilter,
    executeSearch,
    applySearchParams,
    clearSearch,
    buildSearchUrl,
    fetchFacets,
    fetchHeatmap,
    fetchDisplayConfig,
    setHoveredItem,
    clearHoveredItem,
  };

  return (
    <VoyagerSearchContext.Provider value={value}>
      {children}
    </VoyagerSearchContext.Provider>
  );
};

// Hook for using the context
export const useVoyagerSearch = (): VoyagerSearchContextValue => {
  const context = useContext(VoyagerSearchContext);
  if (!context) {
    throw new Error(
      'useVoyagerSearch must be used within a VoyagerSearchProvider',
    );
  }
  return context;
};

// Export for backward compatibility with the original SearchContext
export {
  VoyagerSearchProvider as SearchProvider,
  useVoyagerSearch as useSearch,
};
